import { useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getProviderConfig } from '@/lib/constants/connector'
import { oauthErrorToFriendly } from '@/lib/constants/connector-errors'
import type { ConnectorProvider } from '@/lib/api/types'

// Phase 24 — OAuth error popup (closes AGENTS.md C7).
// Intentionally NOT under `_authenticated`: identical reasoning to the
// success sibling. Popup only uses window.opener.postMessage + close.

const searchSchema = z.object({
  error: fallback(z.string().optional(), undefined),
  connectorId: fallback(z.string().optional(), undefined),
})

const segmentToProvider: Record<string, ConnectorProvider> = {
  google: 'google_drive',
  clickup: 'clickup',
}

export const Route = createFileRoute('/connectors/$provider/error')({
  validateSearch: zodValidator(searchSchema),
  component: ErrorPage,
})

export function ErrorPage() {
  const { provider: segment } = Route.useParams()
  const { error, connectorId } = Route.useSearch()
  const provider = segmentToProvider[segment] ?? 'google_drive'
  const providerLabel = getProviderConfig(provider).label
  const friendly = oauthErrorToFriendly(error)

  useEffect(() => {
    document.title = `Connection failed — ${providerLabel}`

    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          {
            type: 'oauth-callback',
            provider,
            success: false,
            connectorId,
            error: friendly,
          },
          window.location.origin,
        )
      } catch {
        /* cross-origin throw — ignore */
      }
    }

    const closeTimer = setTimeout(() => {
      try {
        window.close()
      } catch {
        /* noop */
      }
    }, 2000)

    return () => {
      clearTimeout(closeTimer)
    }
  }, [provider, connectorId, providerLabel, friendly])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="bg-card rounded-xl shadow-sm border border-border p-8 py-12 flex flex-col items-center gap-3 max-w-md w-full">
        <AlertCircle className="w-6 h-6 text-destructive" />
        <h2 className="text-lg font-medium">Connection failed</h2>
        <p className="text-sm text-foreground text-center">{friendly}</p>
        <p className="text-sm text-muted-foreground text-center">
          This window will close automatically. You can try again from Settings.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            try {
              window.close()
            } catch {
              /* noop */
            }
          }}
        >
          Close
        </Button>
      </div>
    </div>
  )
}
